import styled from '@emotion/styled';
import { useEffect, useRef, useState } from 'react';
import { useDrag, useDrop } from 'react-dnd';
import { getEmptyImage } from 'react-dnd-html5-backend';

import { mergeRefs } from '../../../lib/mergeRefs';
import { ZoneContextMenuContent } from '../zone-context-menu/ZoneContextMenuContent';

import glyphStyles from '../../../components/common/Glyph.module.css';
import { su } from '../../../theme/functions/su';
import { motion } from '../../../theme/motion';
import { theme } from '../../../theme/theme';

import { ContextMenu } from '../../../components/common/context-menu/ContextMenu';
import { ContextMenuButton } from '../../../components/common/context-menu/ContextMenuButton';
import { Glyph } from '../../../components/common/Glyph';
import { dragTypes } from '../../../components/dnd/dragTypes';

const contextMenuButtonClass = 'zoneContextMenuButton';

export function ZoneNavigationItem({
  item,
  byId,
  icon,
  isSelected,
  isEdit,
  isValidDropTarget,
  isParentDragging,
  setDraftZone,
  setEditZoneId,
  onClick,
  onDragBegin,
  onDragEnd,
  onDrop,
  onEditBlur,
  onEditSave,
  EditNameTextField,
}) {
  const domRef = useRef(null);

  const dragItem = {
    type: dragTypes.ZONE,
    id: item.id,
    level: item.level,
    iconUrl: icon(byId[item.id]),
    name: byId[item.id].name,
  };

  const [{ isDragging }, dragRef, preview] = useDrag({
    type: dragTypes.ZONE,
    item: () => {
      dragItem.originalWidth = domRef.current.offsetWidth;
      onDragBegin(dragItem);
      return dragItem;
    },
    end(dragItem, dragMonitor) {
      onDragEnd(dragItem);
    },
    collect(dragMonitor) {
      return {
        isDragging: !!dragMonitor.isDragging(),
      };
    },
  });

  useEffect(() => {
    preview(getEmptyImage());
  }, [preview]);

  const [{ isOver, canDrop }, dropRef] = useDrop({
    accept: [dragTypes.ZONE, dragTypes.DEVICE],
    collect: (dropMonitor) => {
      return {
        isOver: dropMonitor.isOver() && isValidDropTarget && !isDragging,
        canDrop: dropMonitor.canDrop(),
      };
    },
    canDrop(dragItem, dropMonitor) {
      return dragItem.id !== item.id && isValidDropTarget;
    },
    drop(dragItem, monitor) {
      // Drop inside for devices, or zones in the middle area
      onDrop({ type: dragItem.type, dropId: item.id, dragId: dragItem.id, position: 'inside' });
    },
  });

  return (
    <ContextMenu
      content={
        <ZoneContextMenuContent
          zone={byId[item.id]}
          onRenameRequest={() => {
            setEditZoneId(item.id);
          }}
          onCreateRequest={() => {
            setDraftZone({ id: 'create', parentId: item.id });
          }}
        />
      }
    >
      {({ isOpen, onContextMenu }) => {
        return (
          <S.Zone
            ref={!isEdit ? mergeRefs([dragRef, dropRef, domRef]) : null}
            id={item.id}
            style={{ '--level-padding': `${item.level * 20 + 10}px` }}
            data-can-drop={canDrop}
            data-is-selected={isSelected}
            data-is-over={isOver}
            data-is-dragging={isDragging}
            data-is-parent-dragging={isParentDragging}
            data-is-context-menu-open={isOpen}
            onClick={onClick}
            onContextMenu={onContextMenu}
          >
            {icon && (
              <S.ZoneIconWrapper>
                <Glyph
                  source={icon(byId[item.id])}
                  color={theme.color.icon_dark}
                  size={theme.icon.size_default}
                />
              </S.ZoneIconWrapper>
            )}

            {isEdit ? (
              <NameField
                item={item}
                data={byId}
                onEditBlur={onEditBlur}
                onEditSave={onEditSave}
                EditNameTextField={EditNameTextField}
              />
            ) : (
              <>
                <S.ZoneLabel title={byId[item.id].name} isSelected={isSelected}>
                  {byId[item.id].name}
                </S.ZoneLabel>

                <ContextMenuButton
                  className={contextMenuButtonClass}
                  isActive={isSelected}
                  isOpen={isOpen}
                  onPress={onContextMenu}
                />
              </>
            )}
          </S.Zone>
        );
      }}
    </ContextMenu>
  );
}

function NameField({ item, data, onEditBlur, onEditSave, EditNameTextField }) {
  const [name, setName] = useState(data[item.id].name);
  const itemName = data[item.id].name;

  useEffect(() => {
    setName(itemName);
  }, [itemName]);

  return (
    <EditNameTextField
      defaultValue={name}
      onBlur={(event) => {
        if (event.currentTarget.value.length === 0) return onEditBlur();

        const prevName = name;
        setName(event.currentTarget.value);

        function reject() {
          setName(prevName);
        }

        function resolve() {}

        onEditBlur();
        onEditSave({
          id: item.id,
          value: event.currentTarget.value,
          resolve,
          reject,
        });
      }}
      onKeyUp={(event) => {
        switch (event.key) {
          case 'Enter': {
            if (event.currentTarget.value.length === 0) return;
            const prevName = name;
            setName(event.currentTarget.value);

            function reject() {
              setName(prevName);
            }

            function resolve() {}

            onEditBlur();
            onEditSave({
              id: item.id,
              value: event.currentTarget.value,
              resolve,
              reject,
            });
            break;
          }
          // Escape
          case 'Escape':
            onEditBlur();
            break;
          default:
            break;
        }
      }}
    />
  );
}

const S = {};

S.ZoneLabel = styled.span``;

S.Zone = styled.div`
  display: flex;
  position: relative;
  z-index: calc(${theme.zIndex.nav} + 1);
  align-items: center;
  margin: 0 ${su(1)};
  border-radius: ${theme.borderRadius.default};
  padding-inline-start: var(--level-padding);
  outline: 0;
  cursor: pointer;
  user-select: none;
  border: 2px solid transparent;
  min-height: ${su(5)};
  padding-inline-end: ${su(1)};
  transition: all ${motion.duration.micro} ${motion.curve.fast_in};

  ${S.ZoneLabel} {
    display: inline-block;
    flex: 1 1 auto;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    line-height: ${su(4)};
    padding: 0 0 0 ${su(1)};
  }

  &:hover {
    background-color: ${theme.color.background_hover};

    .${glyphStyles.root} {
      color: ${theme.color.icon_dark_hover};
    }
  }

  .${contextMenuButtonClass} {
    opacity: 0;
    transition: opacity ${motion.transition.micro_partial};
    width: 0;
  }

  &[data-is-context-menu-open='true'],
  &:hover {
    padding-inline-end: 0;

    .${contextMenuButtonClass} {
      width: ${su(5)};
      opacity: 1;
    }
  }

  &[data-is-context-menu-open='true'] {
    .${contextMenuButtonClass} {
      opacity: 1;
    }
  }

  &[data-is-over='true'] {
    border-color: ${theme.color.state_drop_line};
    background-color: ${theme.color.state_drop_background};
  }

  &[data-is-dragging='true'],
  &[data-is-parent-dragging='true'] {
    opacity: 0.3;
  }

  &[data-is-selected='true'] {
    &,
    &:hover {
      color: ${theme.color.highlight};

      ${S.ZoneIconWrapper} .${glyphStyles.root} {
        color: ${theme.color.highlight};
      }

      ${S.ZoneLabel} {
        font-weight: ${theme.fontWeight.bold};
      }

      .${contextMenuButtonClass} {
        opacity: 1;
      }
    }
  }
`;

S.ZoneIconWrapper = styled.div`
  flex: 0 0 auto;
  display: flex;
  align-items: center;

  .${glyphStyles.root} {
    color: ${theme.color.icon_dark};
    transition: color ${motion.duration.fast} ${motion.curve.fast_in};
  }
`;
